"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.taxIdTypes = exports.countryCodeToName = exports.stringToNumber = exports.getFormattedAmount = exports.calculateDiscountPercentage = exports.formatTaxDescription = exports.formatCurrency = exports.roundCurrency = exports.formatPrices = void 0;
const numberFormatter = new Intl.NumberFormat([], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});
function formatPrices(price) {
    if (price === null || price === undefined) {
        return;
    }
    return numberFormatter.format(price);
}
exports.formatPrices = formatPrices;
/**
 * Round a currency amount to the nearest smallest currency unit Stripe supports.
 *
 * @example
 * ```ts
 * roundCurrency(0.4999999999999999) // 0.5
 * ```
 * @param { number } amount - the amount to round. Stripe rounds to the nearest smallest currency unit (.50 for USD).
 *
 * @returns { number } the rounded amount
 */
function roundCurrency(amount) {
    /** this is the smallest currency unit Stripe supports for USD */
    if (amount < 0.5) {
        return 0.5;
    }
    return Math.round(amount * 100) / 100;
}
exports.roundCurrency = roundCurrency;
function formatCurrency(amount) {
    let value = amount;
    if (typeof value === 'string') {
        value = stringToNumber(value);
    }
    if (Number.isNaN(value)) {
        throw new Error(`Invalid currency value: ${value}. Must be a number or a string - received ${typeof value}.`);
    }
    return stringToNumber(numberFormatter.format(value));
}
exports.formatCurrency = formatCurrency;
function formatTaxDescription(taxRate) {
    if (typeof taxRate !== 'object') {
        /** tax rates that are strings are not the expanded object, they're an ID */
        throw new Error('Invalid tax rate: must be an expanded object.');
    }
    const { display_name: displayName, percentage, jurisdiction } = taxRate;
    return `${displayName} - ${jurisdiction} (${percentage}%)`;
}
exports.formatTaxDescription = formatTaxDescription;
/** discounts should be applied before taxes */
function calculateDiscountPercentage(amount, discount) {
    return roundCurrency(amount - (amount * discount) / 100);
}
exports.calculateDiscountPercentage = calculateDiscountPercentage;
/**
 * Takes an integer amount (e.g. 1234) and converts it to dollars and cents (e.g. 12.34)
 *
 * We produce this amount using string manipulation instead of dividing by 100 to avoid
 * JS floating-point arithmetic errors.
 *
 * @see {@link https://stackoverflow.com/a/55131236 | Stack Overflow }
 */
function getFormattedAmount(amount) {
    // Left-pad with 0's for amounts less than 10 cents
    const paddedAmount = amount.toString().padStart(2, '0');
    // Add a decimal two places from the end
    const dollarAmount = `${paddedAmount.slice(0, paddedAmount.length - 2)}.${paddedAmount.slice(paddedAmount.length - 2)}`;
    /* istanbul ignore next */
    const formattedDollarAmount = formatPrices(parseFloat(dollarAmount))?.replace('-', '') || '0.00';
    const sign = amount < 0 ? '-$' : '$';
    return `${sign}${formattedDollarAmount}`;
}
exports.getFormattedAmount = getFormattedAmount;
/**
 * Convert a string to a number.
 *
 * @example
 * ```
 * stringToNumber('1,234.56') // 1234.56
 * ```
 *
 * @param string - the string to convert to a number
 *
 * @returns number - the converted number
 *
 */
function stringToNumber(value) {
    if (typeof value !== 'string') {
        throw new Error(`Invalid string: ${value}`);
    }
    const result = Number(value.replace(/,/g, ''));
    if (Number.isNaN(result)) {
        throw new Error(`Invalid number: ${value}`);
    }
    return result;
}
exports.stringToNumber = stringToNumber;
/**
 * Convert a country code to a country name.
 *
 * @param string - the two letter country code to convert to a country name
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames/DisplayNames | MDN - Intl.DisplayNames}
 */
function countryCodeToName(countryCode) {
    const country = new Intl.DisplayNames([], { type: 'region' });
    return country.of(countryCode);
}
exports.countryCodeToName = countryCodeToName;
/**
 * Supported Stripe Tax ID `type` properties.
 *
 * @remarks
 * Placeholder values for tax IDs reflect an expected format for the tax ID type.
 * For example, Brazilian CPF number would be `123.456.789-87`.
 *
 * Stripe MAY return an error if the format of the tax ID does not match the expected format.
 * However, this does not appear to always be the case.
 *
 */
exports.taxIdTypes = [
    {
        country: 'Australia',
        countryCode: 'AU',
        type: 'au_abn',
        description: 'Australian Business Number (AU ABN)',
        placeholder: '12345678912'
    },
    {
        country: 'Australia',
        countryCode: 'AU',
        type: 'au_arn',
        description: 'Australian Taxation Office Reference Number',
        placeholder: '123456789123'
    },
    {
        country: 'Austria',
        countryCode: 'AT',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'ATU12345678'
    },
    {
        country: 'Belgium',
        countryCode: 'BE',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'BE0123456789'
    },
    {
        country: 'Brazil',
        countryCode: 'BR',
        type: 'br_cnpj',
        description: 'Brazilian CNPJ number',
        placeholder: '01.234.456/5432-10'
    },
    {
        country: 'Brazil',
        countryCode: 'BR',
        type: 'br_cpf',
        description: 'Brazilian CPF number',
        placeholder: '123.456.789-87'
    },
    {
        country: 'Bulgaria',
        countryCode: 'BG',
        type: 'bg_uic',
        description: 'Bulgaria Unified Identification Code',
        placeholder: '123456789'
    },
    {
        country: 'Bulgaria',
        countryCode: 'BG',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'BG0123456789'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_bn',
        description: 'Canadian BN',
        placeholder: '123456789'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_gst_hst',
        description: 'Canadian GST/HST number',
        placeholder: '123456789RT0002'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_pst_bc',
        description: 'Canadian PST number (British Columbia)',
        placeholder: 'PST-1234-5678'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_pst_mb',
        description: 'Canadian PST number (Manitoba)',
        placeholder: '123456-7'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_pst_sk',
        description: 'Canadian PST number (Saskatchewan)',
        placeholder: '1234567'
    },
    {
        country: 'Canada',
        countryCode: 'CA',
        type: 'ca_qst',
        description: 'Canadian QST number (Québec)',
        placeholder: '1234567890TQ1234'
    },
    {
        country: 'Chile',
        countryCode: 'CL',
        type: 'cl_tin',
        description: 'Chilean TIN',
        placeholder: '12.345.678-K'
    },
    {
        country: 'Croatia',
        countryCode: 'HR',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'HR12345678912'
    },
    {
        country: 'Cyprus',
        countryCode: 'CY',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'CY12345678Z'
    },
    {
        country: 'Czech Republic',
        countryCode: 'CZ',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'CZ1234567890'
    },
    {
        country: 'Denmark',
        countryCode: 'DK',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'DK12345678'
    },
    {
        country: 'Egypt',
        countryCode: 'EG',
        type: 'eg_tin',
        description: 'Egyptian Tax Identification Number',
        placeholder: '123456789'
    },
    {
        country: 'Estonia',
        countryCode: 'EE',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'EE123456789'
    },
    {
        country: 'EU',
        countryCode: 'EU',
        type: 'eu_oss_vat',
        description: 'European One Stop Shop VAT number for non-Union scheme',
        placeholder: 'EU123456789'
    },
    {
        country: 'Finland',
        countryCode: 'FI',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'FI12345678'
    },
    {
        country: 'France',
        countryCode: 'FR',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'FRAB123456789'
    },
    {
        country: 'Georgia',
        countryCode: 'GE',
        type: 'ge_vat',
        description: 'Georgian VAT',
        placeholder: '123456789'
    },
    {
        country: 'Germany',
        countryCode: 'DE',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'DE123456789'
    },
    {
        country: 'Greece',
        countryCode: 'GR',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'EL123456789'
    },
    {
        country: 'Hong Kong',
        countryCode: 'HK',
        type: 'hk_br',
        description: 'Hong Kong BR number',
        placeholder: '12345678'
    },
    {
        country: 'Hungary',
        countryCode: 'HU',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'HU12345678912'
    },
    {
        country: 'Hungary',
        countryCode: 'HU',
        type: 'hu_tin',
        description: 'Hungary tax number (adószám)',
        placeholder: '12345678-1-23'
    },
    {
        country: 'Iceland',
        countryCode: 'IS',
        type: 'is_vat',
        description: 'Icelandic VAT',
        placeholder: '123456'
    },
    {
        country: 'India',
        countryCode: 'IN',
        type: 'in_gst',
        description: 'Indian GST number',
        placeholder: '12ABCDE3456FGZH'
    },
    {
        country: 'Indonesia',
        countryCode: 'ID',
        type: 'id_npwp',
        description: 'Indonesian NPWP number',
        placeholder: '12.345.678.9-012.345'
    },
    {
        country: 'Ireland',
        countryCode: 'IE',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'IE1234567AB'
    },
    {
        country: 'Israel',
        countryCode: 'IL',
        type: 'il_vat',
        description: 'Israel VAT',
        placeholder: '12345'
    },
    {
        country: 'Italy',
        countryCode: 'IT',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'IT12345678912'
    },
    {
        country: 'Japan',
        countryCode: 'JP',
        type: 'jp_cn',
        description: 'Japanese Corporate Number',
        placeholder: '1234567891234'
    },
    {
        country: 'Japan',
        countryCode: 'JP',
        type: 'jp_rn',
        description: 'Japanese Registered Foreign Businesses Number',
        placeholder: '12345'
    },
    {
        country: 'Japan',
        countryCode: 'JP',
        type: 'jp_trn',
        description: 'Japanese Tax Registration Number',
        placeholder: 'T1234567891234'
    },
    {
        country: 'Kenya',
        countryCode: 'KE',
        type: 'ke_pin',
        description: 'Kenya PIN',
        placeholder: 'P000111111A'
    },
    {
        country: 'Latvia',
        countryCode: 'LV',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'LV12345678912'
    },
    {
        country: 'Liechtenstein',
        countryCode: 'LI',
        type: 'li_uid',
        description: 'Liechtensteinian UID number',
        placeholder: 'CHE123456789'
    },
    {
        country: 'Lithuania',
        countryCode: 'LT',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'LT123456789123'
    },
    {
        country: 'Luxembourg',
        countryCode: 'LU',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'LU12345678'
    },
    {
        country: 'Malaysia',
        countryCode: 'MY',
        type: 'my_frp',
        description: 'Malaysian FRP number',
        placeholder: '12345678'
    },
    {
        country: 'Malaysia',
        countryCode: 'MY',
        type: 'my_itn',
        description: 'Malaysian ITN',
        placeholder: 'C 1234567890'
    },
    {
        country: 'Malaysia',
        countryCode: 'MY',
        type: 'my_sst',
        description: 'Malaysian SST number',
        placeholder: 'A12-3456-78912345'
    },
    {
        country: 'Malta',
        countryCode: 'MT',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'MT12345678'
    },
    {
        country: 'Mexico',
        countryCode: 'MX',
        type: 'mx_rfc',
        description: 'Mexican RFC number',
        placeholder: 'ABC010203AB9'
    },
    {
        country: 'Netherlands',
        countryCode: 'NL',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'NL123456789B12'
    },
    {
        country: 'New Zealand',
        countryCode: 'NZ',
        type: 'nz_gst',
        description: 'New Zealand GST number',
        placeholder: '123456789'
    },
    {
        country: 'Norway',
        countryCode: 'NO',
        type: 'no_vat',
        description: 'Norwegian VAT number',
        placeholder: '123456789MVA'
    },
    {
        country: 'Philippines',
        countryCode: 'PH',
        type: 'ph_tin',
        description: 'Philippines TIN',
        placeholder: '123456789012'
    },
    {
        country: 'Poland',
        countryCode: 'PL',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'PL1234567890'
    },
    {
        country: 'Portugal',
        countryCode: 'PT',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'PT123456789'
    },
    {
        country: 'Romania',
        countryCode: 'RO',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'RO1234567891'
    },
    {
        country: 'Russia',
        countryCode: 'RU',
        type: 'ru_inn',
        description: 'Russian INN',
        placeholder: '1234567891'
    },
    {
        country: 'Russia',
        countryCode: 'RU',
        type: 'ru_kpp',
        description: 'Russian KPP',
        placeholder: '123456789'
    },
    {
        country: 'Saudi Arabia',
        countryCode: 'SA',
        type: 'sa_vat',
        description: 'Saudi Arabia VAT',
        placeholder: '`123456789012345'
    },
    {
        country: 'Singapore',
        countryCode: 'SG',
        type: 'sg_gst',
        description: 'Singaporean GST',
        placeholder: 'M12345678X'
    },
    {
        country: 'Singapore',
        countryCode: 'SG',
        type: 'sg_uen',
        description: 'Singaporean UEN',
        placeholder: '123456789F'
    },
    {
        country: 'Slovakia',
        countryCode: 'SK',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'SK1234567891'
    },
    {
        country: 'Slovenia',
        countryCode: 'SI',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'SI12345678'
    },
    {
        country: 'Slovenia',
        countryCode: 'SI',
        type: 'si_tin',
        description: 'Slovenia tax number',
        placeholder: '12345678'
    },
    {
        country: 'South Africa',
        countryCode: 'ZA',
        type: 'za_vat',
        description: 'South African VAT number',
        placeholder: '4123456789'
    },
    {
        country: 'South Korea',
        countryCode: 'KR',
        type: 'kr_brn',
        description: 'Korean BRN',
        placeholder: '123-45-67890'
    },
    {
        country: 'Spain',
        countryCode: 'ES',
        type: 'es_cif',
        description: 'Spanish NIF',
        placeholder: 'A12345678'
    },
    {
        country: 'Spain',
        countryCode: 'ES',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'ESA1234567Z'
    },
    {
        country: 'Sweden',
        countryCode: 'SE',
        type: 'eu_vat',
        description: 'European VAT number',
        placeholder: 'SE123456789123'
    },
    {
        country: 'Switzerland',
        countryCode: 'CH',
        type: 'ch_vat',
        description: 'Switzerland VAT number',
        placeholder: 'CHE-123.456.789 MWST'
    },
    {
        country: 'Taiwan',
        countryCode: 'TW',
        type: 'tw_vat',
        description: 'Taiwanese VAT',
        placeholder: '12345678'
    },
    {
        country: 'Thailand',
        countryCode: 'TH',
        type: 'th_vat',
        description: 'Thai VAT',
        placeholder: '1234567891234'
    },
    {
        country: 'Turkey',
        countryCode: 'TR',
        type: 'tr_tin',
        description: 'Turkish Tax Identification Number',
        placeholder: '123456789'
    },
    {
        country: 'Ukraine',
        countryCode: 'UA',
        type: 'ua_vat',
        description: 'Ukrainian VAT',
        placeholder: '123456789'
    },
    {
        country: 'United Arab Emirates',
        countryCode: 'AE',
        type: 'ae_trn',
        description: 'United Arab Emirates TRN',
        placeholder: '123456789012345'
    },
    {
        country: 'United Kingdom',
        countryCode: 'GB',
        type: 'eu_vat',
        description: 'Northern Ireland VAT number',
        placeholder: 'XI123456789'
    },
    {
        country: 'United Kingdom',
        countryCode: 'GB',
        type: 'gb_vat',
        description: 'United Kingdom VAT number',
        placeholder: 'GB123456789'
    },
    {
        country: 'United States',
        countryCode: 'US',
        type: 'us_ein',
        description: 'United States EIN',
        placeholder: '12-3456789'
    }
];
//# sourceMappingURL=purchaseUtils.js.map